import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_URLS, BACKEND_URL } from "../config";
import { apiRequest } from "../utils/apiRequest";
import AccessDenied from "../Components/AccessDenied";
import { FaIndustry } from "react-icons/fa6";
import { MdAdminPanelSettings } from "react-icons/md";
import { FiEdit, FiChevronUp, FiChevronDown, FiArrowLeft } from "react-icons/fi";
import "./../css/Styles.css";
import "./master/Vendors.css";
import PhoneField from "../Components/PhoneField";
import { TbHistory } from "react-icons/tb";
import {
    INVITE_PLACEHOLDERS, DEFAULT_INVITE_SUBJECT, DEFAULT_INVITE_MESSAGE, SAMPLE_INVITE_VALUES, fillInviteTemplate,
} from "../utils/inviteMailTemplate";

const EMPTY_BANK = {
    beneficiary: "", bankName: "", address: "", branch: "", accountNumber: "", swiftCode: "",
    codes: [],
};

// Which domestic routing codes apply depends on whether the company is registered
// in India ("Domestic" — IFSC) or elsewhere ("Overseas" — BCN/BLZ/IBAN, any
// combination of which a given bank may require at once).
const DOMESTIC_CODE_TYPES = ["IFSC Code"];
const OVERSEAS_CODE_TYPES = ["BCN Code", "BLZ Code", "IBAN"];

// Reads a company's saved bank codes into the {codeType, value}[] shape the form
// uses, migrating older single-field shapes (ifscCode/iban/blz/bcn) if that's what
// was saved before this became a free-form list.
const bankCodesOf = (bankDetails) => {
    if (bankDetails?.codes?.length) return bankDetails.codes;
    const legacy = [
        ["IFSC Code", bankDetails?.ifscCode],
        ["IBAN", bankDetails?.iban],
        ["BLZ Code", bankDetails?.blz],
        ["BCN Code", bankDetails?.bcn],
    ].filter(([, value]) => value).map(([codeType, value]) => ({ codeType, value }));
    return legacy;
};

// Read/write one named entry out of the {codeType, value}[] list.
const codeValue = (codes, type) => (codes || []).find(c => c.codeType === type)?.value || "";
const withCodeValue = (codes, type, value) => {
    const list = codes || [];
    return list.some(c => c.codeType === type)
        ? list.map(c => c.codeType === type ? { ...c, value } : c)
        : [...list, { codeType: type, value }];
};

// ── Coordinator incentive tier tables ─────────────────────────────────────
// Each table is 4 rows: min/max/rate, chained so tier[i].max === tier[i+1].min
// (only the breakpoints and rates are ever edited directly; min values follow).
// The last row's max is always null — "this % and above", unbounded.
const setTierBreakpoint = (tiers, i, value) => {
    const next = tiers.map(t => ({ ...t }));
    next[i].max = value;
    if (next[i + 1]) next[i + 1].min = value;
    return next;
};
const setTierRate = (tiers, i, value) => {
    const next = tiers.map(t => ({ ...t }));
    next[i].rate = value;
    return next;
};

const TierTableEditor = ({ label, tiers, onChange }) => (
    <div>
        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>{label}</div>
        <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead>
                <tr style={{ color: "#888", textAlign: "left" }}>
                    <th style={{ padding: "4px 6px" }}>Margin range</th>
                    <th style={{ padding: "4px 6px", width: 110 }}>Rate (₹/win)</th>
                </tr>
            </thead>
            <tbody>
                {tiers.map((t, i) => (
                    <tr key={i} style={{ borderTop: "1px solid #eee" }}>
                        <td style={{ padding: "4px 6px", whiteSpace: "nowrap" }}>
                            {i === 0 ? "Below " : `${tiers[i - 1].max}% – `}
                            {t.max !== null ? (
                                <>
                                    <input
                                        type="number" min={0} max={100} value={t.max}
                                        onChange={e => onChange(setTierBreakpoint(tiers, i, Number(e.target.value)))}
                                        style={{ width: 46 }}
                                    /> %
                                </>
                            ) : "and above"}
                        </td>
                        <td style={{ padding: "4px 6px" }}>
                            <input
                                type="number" min={0} value={t.rate}
                                onChange={e => onChange(setTierRate(tiers, i, Number(e.target.value)))}
                                style={{ width: 90 }}
                            />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// Quarterly slabs: same chained min/max layout as the tier tables above, but
// bounds are edited in lakh (stored as INR) and the rate is a % of offer cost.
const LAKH = 100000;

const QuarterlySlabEditor = ({ slabs, onChange }) => (
    <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
        <thead>
            <tr style={{ color: "#888", textAlign: "left" }}>
                <th style={{ padding: "4px 6px" }}>Offer cost band (&#8377; lakh)</th>
                <th style={{ padding: "4px 6px", width: 110 }}>Rate (%)</th>
            </tr>
        </thead>
        <tbody>
            {slabs.map((s, i) => (
                <tr key={i} style={{ borderTop: "1px solid #eee" }}>
                    <td style={{ padding: "4px 6px", whiteSpace: "nowrap" }}>
                        {i === 0 ? "Up to " : `${slabs[i - 1].max / LAKH}L – `}
                        {s.max !== null ? (
                            <>
                                <input
                                    type="number" min={0} step={0.5} value={s.max / LAKH}
                                    onChange={e => onChange(setTierBreakpoint(slabs, i, Math.round(Number(e.target.value) * LAKH)))}
                                    style={{ width: 56 }}
                                /> L
                            </>
                        ) : "and above"}
                    </td>
                    <td style={{ padding: "4px 6px" }}>
                        <input
                            type="number" min={0} max={100} step={0.05} value={s.ratePercent}
                            onChange={e => {
                                const next = slabs.map(x => ({ ...x }));
                                next[i].ratePercent = Number(e.target.value);
                                onChange(next);
                            }}
                            style={{ width: 90 }}
                        />
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);

const EMPTY = {
    companyName: "", address: "", country: "India", state: "", city: "", pinCode: "",
    companyType: "Domestic", taxIdType: "GST", gstVatNumber: "",
    msmeNumber: "", hsnCode: "",
    jobInitial: "",
    adminFullName: "", adminEmail: "", adminMobile: "", adminPassword: "", adminDesignation: "",
    termsOfService: "",
    confirmationClauses: "",
    inviteSubject: "",
    inviteMessage: "",
    bankDetails: EMPTY_BANK,
    signerName: "",
    pastRecordsLink: "",
};

// Maps a saved company onto the form's field shape.
const formFromCompany = (c) => ({
    companyName: c.companyName || "", address: c.address || "", country: c.country || "India",
    state: c.state || "", city: c.city || "", pinCode: c.pinCode || "",
    companyType: c.companyType || (c.taxIdType === "VAT" ? "Overseas" : "Domestic"),
    taxIdType: c.taxIdType || "GST", gstVatNumber: c.gstVatNumber || "",
    msmeNumber: c.msmeNumber || "", hsnCode: c.hsnCode || "",
    jobInitial: c.jobInitial || "",
    adminFullName: "", adminEmail: "", adminMobile: "", adminPassword: "", adminDesignation: "",
    termsOfService: (c.termsOfService || []).join("\n"),
    confirmationClauses: (c.confirmationClauses || []).join("\n"),
    inviteSubject: c.inviteMailTemplate?.subject || "",
    inviteMessage: c.inviteMailTemplate?.message || "",
    bankDetails: { ...EMPTY_BANK, ...(c.bankDetails || {}), codes: bankCodesOf(c.bankDetails) },
    signerName: c.signature?.signerName || "",
    pastRecordsLink: c.pastRecordsLink || "",
});

// One page for all three company screens, picked by route:
//   /company-management/new       → mode "add"
//   /company-management/:id       → mode "view" (read-only)
//   /company-management/:id/edit  → mode "edit"
const CompanyForm = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams();
    const { pathname } = useLocation();
    const mode = !id ? "add" : pathname.endsWith("/edit") ? "edit" : "view";
    const isView = mode === "view";

    const [companies, setCompanies] = useState([]);
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [denied, setDenied] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [stampFile, setStampFile] = useState(null);
    const [stampPreview, setStampPreview] = useState(null);
    const [signatureFile, setSignatureFile] = useState(null);
    const [signaturePreview, setSignaturePreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(true);
    const [adminOpen, setAdminOpen] = useState(true);
    const [termsOpen, setTermsOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pastRecordsOpen, setPastRecordsOpen] = useState(false);
    const [inviteMailOpen, setInviteMailOpen] = useState(false);
    // Which invite field a placeholder chip inserts into — the last one focused.
    const [inviteField, setInviteField] = useState("inviteMessage");
    const inviteSubjectRef = useRef(null);
    const inviteMessageRef = useRef(null);
    const [bankOpen, setBankOpen] = useState(false);
    const [signatureOpen, setSignatureOpen] = useState(false);
    const [incentiveOpen, setIncentiveOpen] = useState(false);
    const [incentive, setIncentive] = useState(null); // null = loading/not loaded; { __denied: true } = no permission
    const [incentiveSaving, setIncentiveSaving] = useState(false);

    const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };
    const backToList = (msg) => navigate("/company-management", msg ? { state: { toast: msg } } : undefined);

    const loadIncentiveSettings = async (companyId) => {
        setIncentive(null);
        const r = await apiRequest(`${API_URLS.INCENTIVE}/settings/${companyId}`, {}, token);
        if (!r) return;
        if (r.denied || !r.ok) { setIncentive({ __denied: true }); return; }
        setIncentive(r.data.data);
    };

    // The full list is needed in every mode to grey out job initials other companies already use.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const r = await apiRequest(API_URLS.COMPANIES, {}, token);
            if (cancelled || !r) return;
            if (r.denied) { setDenied(r); setLoading(false); return; }
            const list = r.ok ? (r.data || []) : [];
            setCompanies(list);

            const c = id ? list.find(x => String(x.id) === id) || null : null;
            setCompany(c);
            setForm(c ? formFromCompany(c) : EMPTY);
            setLogoFile(null); setStampFile(null); setSignatureFile(null);
            setLogoPreview(c?.logo ? `${BACKEND_URL}${c.logo}` : null);
            setStampPreview(c?.signature?.companyStamp ? `${BACKEND_URL}${c.signature.companyStamp}` : null);
            setSignaturePreview(c?.signature?.signerSignature ? `${BACKEND_URL}${c.signature.signerSignature}` : null);
            setLoading(false);
            if (c) loadIncentiveSettings(c.id);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, mode, token]);

    const handleSaveIncentive = async () => {
        if (!company || !incentive || incentive.__denied) return;
        setIncentiveSaving(true);
        const {
            enabled, othersTiers, indiaTiers, manmonthRate, manmonthGateOthers, manmonthGateIndia, combinedWonFloor,
            quarterlyEnabled, quarterlyMinRevenueINR, quarterlySlabs,
        } = incentive;
        const r = await apiRequest(
            `${API_URLS.INCENTIVE}/settings/${company.id}`,
            {
                method: "PUT",
                body: {
                    enabled, othersTiers, indiaTiers, manmonthRate, manmonthGateOthers, manmonthGateIndia, combinedWonFloor,
                    quarterlyEnabled, quarterlyMinRevenueINR, quarterlySlabs,
                },
            },
            token
        );
        setIncentiveSaving(false);
        if (!r) return;
        if (!r.ok) { showToast(r.data?.message || "Failed to save incentive settings.", "error"); return; }
        setIncentive(r.data.data);
        showToast("Incentive settings saved!");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isView) return;
        setSaving(true);
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => {
            if (k === "termsOfService" || k === "confirmationClauses" || k === "bankDetails" || k === "inviteSubject" || k === "inviteMessage") return;
            if (v) fd.append(k, v);
        });
        const toLines = (text) => text.split("\n").map(l => l.trim()).filter(Boolean);
        fd.append("termsOfService", JSON.stringify(toLines(form.termsOfService)));
        fd.append("confirmationClauses", JSON.stringify(toLines(form.confirmationClauses)));
        fd.append("bankDetails", JSON.stringify(form.bankDetails));
        fd.append("inviteMailTemplate", JSON.stringify({ subject: form.inviteSubject, message: form.inviteMessage }));
        if (logoFile) fd.append("logo", logoFile);
        if (stampFile) fd.append("companyStamp", stampFile);
        if (signatureFile) fd.append("signerSignature", signatureFile);
        const isEdit = mode === "edit";
        const r = await apiRequest(
            isEdit ? `${API_URLS.COMPANIES}/${company.id}` : API_URLS.COMPANIES,
            { method: isEdit ? "PUT" : "POST", body: fd, isFormData: true }, token
        );
        setSaving(false);
        if (!r) return;
        if (!r.ok) { showToast(r.data?.message || r.message || "Failed.", "error"); return; }
        backToList(isEdit ? "Company updated!" : "Company created!");
    };

    // Inserts "{key}" at the cursor of the last-focused invite field (subject or message).
    const insertInvitePlaceholder = (key) => {
        const el = (inviteField === "inviteSubject" ? inviteSubjectRef : inviteMessageRef).current;
        const current = form[inviteField] || "";
        const tag = `{${key}}`;
        const start = el?.selectionStart ?? current.length;
        const end = el?.selectionEnd ?? current.length;
        setForm(p => ({ ...p, [inviteField]: current.slice(0, start) + tag + current.slice(end) }));
        requestAnimationFrame(() => {
            if (!el) return;
            el.focus();
            el.setSelectionRange(start + tag.length, start + tag.length);
        });
    };

    const inviteFieldStyle = {
        width: "100%", boxSizing: "border-box", padding: "10px 12px", fontSize: 12, lineHeight: 1.6,
        border: "1px solid #d1d5db", fontFamily: "inherit", color: "#333", background: "#fff", outline: "none",
    };

    const takenInitials = new Set(
        companies
            .filter(c => !company || c.id !== company.id)
            .map(c => c.jobInitial)
            .filter(Boolean)
    );

    if (loading) return <div style={{ padding: "2rem", textAlign: "center", color: "#888" }}>Loading company…</div>;
    if (denied) return <AccessDenied message={denied.message} />;
    if (mode !== "add" && !company) {
        return (
            <div style={{ padding: "2rem", textAlign: "center", color: "#888" }}>
                <p>Company not found.</p>
                <button className="cancel-btn" onClick={() => backToList()}>Back to companies</button>
            </div>
        );
    }

    const title = mode === "add" ? "Add New Company" : mode === "edit" ? `Edit Company — ${company.companyName}` : company.companyName;

    return (
        <div className="vendor-page">
            {toast && (
                <div style={{
                    position: "fixed", top: 70, right: 20, zIndex: 9999, padding: "10px 18px", fontWeight: 500, fontSize: 13,
                    background: toast.type === "error" ? "#fee2e2" : "#dcfce7",
                    color: toast.type === "error" ? "#991b1b" : "#166534",
                    border: `1px solid ${toast.type === "error" ? "#fca5a5" : "#86efac"}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
                }}>
                    {toast.msg}
                </div>
            )}

            <div className="management-card">
                <div className="management-header" style={{ cursor: "default" }}>
                    <div className="header-title">
                        <button type="button" onClick={() => backToList()} title="Back to companies"
                            style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", display: "flex", padding: 0 }}>
                            <FiArrowLeft size={18} />
                        </button>
                        <h2>{title}</h2>
                        {isView && <span className={`status-badge ${company.status}`}>{company.status}</span>}
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* In view mode every input, select and button inside is disabled at once;
                        the section headers are plain divs so they still expand/collapse. */}
                    <fieldset disabled={isView} style={{ border: "none", margin: 0, padding: 0, minWidth: 0 }}>
                        <div className="Company-management-add-wrapper" style={{ padding: "2rem" , gap : "2rem" }}>

                            <div className="Company-management-company-details" style={{ width: "50%" }}>
                                <div className="sub-card-header" onClick={() => setDetailsOpen(o => !o)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <h2><FaIndustry /> Company Details</h2>
                                    {detailsOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                </div>
                                {detailsOpen && <div className="form-grid">
                                    <div className="form-field">
                                        <label>Company Name <span className="required">*</span></label>
                                        <input name="companyName" value={form.companyName} onChange={e => setForm(p => ({ ...p, [e.target.name]: e.target.value }))} required placeholder="Company name" />
                                    </div>
                                    <div className="form-field">
                                        <label>Logo</label>
                                        <div className="upload-controls">
                                            {logoPreview && <img src={logoPreview} alt="logo" style={{ width: 30, height: 30, objectFit: "cover", margin: "0 4px", flexShrink: 0 }} />}
                                            <label htmlFor="comp-logo" className="custom-file-upload">Choose Logo</label>
                                            <input type="file" id="comp-logo" className="file-input" accept="image/*"
                                                onChange={e => { const f = e.target.files[0]; if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); } }} />
                                            <span className="file-name">{logoFile ? logoFile.name : "No file chosen"}</span>
                                        </div>
                                        <p className="upload-info">Max 1 MB · JPG, PNG, WEBP</p>
                                    </div>
                                    <div className="form-field full-width-sm">
                                        <label>Address <span className="required">*</span></label>
                                        <input name="address" value={form.address} onChange={e => setForm(p => ({ ...p, [e.target.name]: e.target.value }))} required placeholder="Street address" />
                                    </div>
                                    {["country", "state", "city", "pinCode"].map(f => (
                                        <div className="form-field" key={f}>
                                            <label style={{ textTransform: "capitalize" }}>{f === "pinCode" ? "PIN Code" : f} <span className="required">*</span></label>
                                            <input name={f} value={form[f]} onChange={e => setForm(p => ({ ...p, [e.target.name]: e.target.value }))} required placeholder={f === "pinCode" ? "PIN / ZIP" : f} />
                                        </div>
                                    ))}
                                    <div className="form-field">
                                        <label>Company Type <span className="required">*</span></label>
                                        <select
                                            name="companyType"
                                            value={form.companyType}
                                            onChange={e => {
                                                const companyType = e.target.value;
                                                setForm(p => ({
                                                    ...p, companyType,
                                                    taxIdType: companyType === "Overseas" ? "VAT" : "GST",
                                                    msmeNumber: companyType === "Overseas" ? "" : p.msmeNumber,
                                                }));
                                            }}
                                        >
                                            <option value="Domestic">Domestic</option>
                                            <option value="Overseas">Overseas</option>
                                        </select>
                                    </div>
                                    <div className="form-field">
                                        <label>{form.taxIdType} Number</label>
                                        <input
                                            name="gstVatNumber"
                                            value={form.gstVatNumber}
                                            onChange={e => setForm(p => ({ ...p, [e.target.name]: e.target.value }))}
                                            placeholder={`Optional — ${form.taxIdType} number`}
                                        />
                                    </div>
                                    {form.companyType !== "Overseas" && (
                                        <div className="form-field">
                                            <label>MSME Number</label>
                                            <input name="msmeNumber" value={form.msmeNumber} onChange={e => setForm(p => ({ ...p, [e.target.name]: e.target.value }))} placeholder="Optional" />
                                        </div>
                                    )}
                                    <div className="form-field">
                                        <label>HSN Code</label>
                                        <input name="hsnCode" value={form.hsnCode} onChange={e => setForm(p => ({ ...p, [e.target.name]: e.target.value }))} placeholder="Optional" />
                                    </div>
                                    <div className="form-field full-width-sm">
                                        <label>Job Number Initial <span className="required">*</span></label>
                                        <p style={{ fontSize: 11, color: "#666", margin: "2px 0 8px" }}>
                                            This letter appears in every job number for this company (e.g. A123<strong>{form.jobInitial || "?"}</strong>001). Greyed-out letters are taken.
                                        </p>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                                            {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map(letter => {
                                                const taken = takenInitials.has(letter);
                                                const selected = form.jobInitial === letter;
                                                return (
                                                    <button
                                                        key={letter}
                                                        type="button"
                                                        disabled={taken}
                                                        onClick={() => setForm(p => ({ ...p, jobInitial: p.jobInitial === letter ? "" : letter }))}
                                                        style={{
                                                            width: 34, height: 34,
                                                            border: `1px solid ${selected ? "#1a2a4d" : taken ? "#e5e7eb" : "#d1d5db"}`,
                                                            background: selected ? "#1a2a4d" : taken ? "#f3f4f6" : "#fff",
                                                            color: selected ? "#fff" : taken ? "#c7c7c7" : "#374151",
                                                            fontWeight: 600, fontSize: 13,
                                                            cursor: taken ? "not-allowed" : "pointer",
                                                            transition: "all 0.1s",
                                                        }}
                                                        title={taken ? "Already used by another company" : letter}
                                                    >
                                                        {letter}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>}
                            </div>

                            {mode === "add" && (
                                <>
                                    <div className="Company-management-companyadmin-details">
                                        <div className="sub-card-header" onClick={() => setAdminOpen(o => !o)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <h2><MdAdminPanelSettings /> Company Admin</h2>
                                            {adminOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                        </div>
                                        {adminOpen && <div className="form-grid">
                                            {[
                                                ["adminFullName", "Full Name", "text", "Admin full name", true],
                                                ["adminEmail", "Email", "email", "Admin email", true],
                                                ["adminMobile", "Mobile Number", "tel", "10-digit mobile", true],
                                                ["adminPassword", "Password", "password", "Min 6 characters", true],
                                                ["adminDesignation", "Designation", "text", "e.g. Director", false],
                                            ].map(([name, label, type, ph, req]) => (
                                                <div className="form-field" key={name}>
                                                    {name === "adminMobile" ? (
                                                        <PhoneField
                                                            label={label}
                                                            value={form[name]}
                                                            onChange={(phone) => setForm(p => ({ ...p, [name]: phone }))}
                                                            required={req}
                                                        />
                                                    ) : (
                                                        <>
                                                            <label>{label} {req && <span className="required">*</span>}</label>
                                                            <input name={name} type={type} value={form[name]}
                                                                onChange={e => setForm(p => ({ ...p, [e.target.name]: e.target.value }))}
                                                                required={req} placeholder={ph} />
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>}
                                    </div>
                                </>
                            )}
                        </div>

                            <div style={{ margin: "0 2rem 2rem 2rem" }}>
                                <div className="sub-card-header" onClick={() => setTermsOpen(o => !o)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <h2 style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16">
                                            <path d="M5 10.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0-2a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5z"/>
                                            <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2zm0 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3z"/>
                                        </svg>
                                        Work Order Terms of Service
                                    </h2>
                                    {termsOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                </div>
                                {termsOpen && (
                                    <div style={{ padding: "20px", border: "1px solid #ccc" }}>
                                        <p style={{ fontSize: 12, color: "#666", margin: "0 0 8px" }}>
                                            Enter each clause on a separate line. Numbers are added automatically in the PDF.
                                            Leave blank to use the default APS Group terms.
                                        </p>
                                        <textarea
                                            value={form.termsOfService}
                                            onChange={e => setForm(p => ({ ...p, termsOfService: e.target.value }))}
                                            rows={10}
                                            placeholder={"Normal Working duration for maximum 8 hours on site...\nWorking beyond normal working hours, Hourly rate will be calculated on pro-rata basis...\n..."}
                                            style={{
                                                width: "100%", boxSizing: "border-box", padding: "10px 12px", fontSize: 12, lineHeight: 1.6,
                                                border: "1px solid #d1d5db", resize: "vertical", fontFamily: "inherit",
                                                color: "#333", background: "#fff", outline: "none",
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div style={{ margin: "0 2rem 2rem 2rem" }}>
                                <div className="sub-card-header" onClick={() => setConfirmOpen(o => !o)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <h2 style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16">
                                            <path d="M10.854 7.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 9.793l2.646-2.647a.5.5 0 0 1 .708 0z"/>
                                            <path d="M3 0h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2zm0 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H3z"/>
                                        </svg>
                                        Confirmation of Understanding Clauses
                                    </h2>
                                    {confirmOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                </div>
                                {confirmOpen && (
                                    <div style={{ padding: "20px", border: "1px solid #ccc" }}>
                                        <p style={{ fontSize: 12, color: "#666", margin: "0 0 8px" }}>
                                            Enter each clause on a separate line. Numbers are added automatically in the PDF.
                                            Leave blank to use the default APS Group confirmation clauses.
                                        </p>
                                        <textarea
                                            value={form.confirmationClauses}
                                            onChange={e => setForm(p => ({ ...p, confirmationClauses: e.target.value }))}
                                            rows={10}
                                            placeholder={"I acknowledge receipt of the inspection documents...\nI agree to confirm my attendance and coordinate the inspection time...\n..."}
                                            style={{
                                                width: "100%", boxSizing: "border-box", padding: "10px 12px", fontSize: 12, lineHeight: 1.6,
                                                border: "1px solid #d1d5db", resize: "vertical", fontFamily: "inherit",
                                                color: "#333", background: "#fff", outline: "none",
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div style={{ margin: "0 2rem 2rem 2rem" }}>
                                <div className="sub-card-header" onClick={() => setInviteMailOpen(o => !o)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <h2 style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16">
                                            <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2zm13 2.383-4.708 2.825L15 11.105V5.383zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741zM1 11.105l4.708-2.897L1 5.383v5.722z"/>
                                        </svg>
                                        Inspector Invitation Mail Format
                                    </h2>
                                    {inviteMailOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                </div>
                                {inviteMailOpen && (
                                    <div style={{ padding: "20px", border: "1px solid #ccc" }}>
                                        <p style={{ fontSize: 12, color: "#666", margin: "0 0 8px" }}>
                                            Subject and message used when inviting inspectors from an enquiry. Click a placeholder to insert it
                                            where your cursor is — it is replaced with the enquiry's real details. Leave blank to use the default format.
                                        </p>

                                        {!isView && (
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "0 0 12px" }}>
                                                {INVITE_PLACEHOLDERS.map(([key, label]) => (
                                                    <button
                                                        key={key}
                                                        type="button"
                                                        title={label}
                                                        onMouseDown={e => e.preventDefault()}
                                                        onClick={() => insertInvitePlaceholder(key)}
                                                        style={{ fontSize: 11, padding: "3px 8px", border: "1px solid #c7d2fe", background: "#eef2ff", color: "#3730a3", borderRadius: 4, cursor: "pointer" }}
                                                    >
                                                        {`{${key}}`}
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        <label style={{ display: "block", fontSize: 12, fontWeight: 600, margin: "0 0 4px" }}>Subject</label>
                                        <input
                                            ref={inviteSubjectRef}
                                            type="text"
                                            value={form.inviteSubject}
                                            onFocus={() => setInviteField("inviteSubject")}
                                            onChange={e => setForm(p => ({ ...p, inviteSubject: e.target.value }))}
                                            placeholder={DEFAULT_INVITE_SUBJECT}
                                            style={{ ...inviteFieldStyle, marginBottom: 12 }}
                                        />

                                        <label style={{ display: "block", fontSize: 12, fontWeight: 600, margin: "0 0 4px" }}>Message</label>
                                        <textarea
                                            ref={inviteMessageRef}
                                            value={form.inviteMessage}
                                            onFocus={() => setInviteField("inviteMessage")}
                                            onChange={e => setForm(p => ({ ...p, inviteMessage: e.target.value }))}
                                            rows={14}
                                            placeholder={DEFAULT_INVITE_MESSAGE}
                                            style={{ ...inviteFieldStyle, resize: "vertical" }}
                                        />

                                        {!isView && (
                                            <div style={{ display: "flex", gap: 8, margin: "8px 0 0" }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setForm(p => ({ ...p, inviteSubject: DEFAULT_INVITE_SUBJECT, inviteMessage: DEFAULT_INVITE_MESSAGE }))}
                                                    style={{ fontSize: 12, padding: "5px 10px", border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}
                                                >
                                                    Start from default format
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setForm(p => ({ ...p, inviteSubject: "", inviteMessage: "" }))}
                                                    style={{ fontSize: 12, padding: "5px 10px", border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}
                                                >
                                                    Reset to default
                                                </button>
                                            </div>
                                        )}

                                        {(() => {
                                            const values = { ...SAMPLE_INVITE_VALUES, companyName: form.companyName || "Your Company" };
                                            const subject = fillInviteTemplate(form.inviteSubject.trim() ? form.inviteSubject : DEFAULT_INVITE_SUBJECT, values);
                                            const message = fillInviteTemplate(form.inviteMessage.trim() ? form.inviteMessage : DEFAULT_INVITE_MESSAGE, values);
                                            return (
                                                <div style={{ marginTop: 16 }}>
                                                    <div style={{ fontSize: 12, fontWeight: 600, margin: "0 0 4px" }}>Preview (sample data)</div>
                                                    <div style={{ border: "1px dashed #bbb", background: "#fafafa", padding: "12px 14px", fontSize: 12, lineHeight: 1.7 }}>
                                                        <div style={{ marginBottom: 8 }}><strong>Subject:</strong> {subject}</div>
                                                        <div style={{ whiteSpace: "pre-wrap" }}>{message}</div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                            <div style={{ margin: "0 2rem 2rem 2rem" }}>
                                <div className="sub-card-header" onClick={() => setBankOpen(o => !o)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <h2 style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16">
                                            <path d="M8 0 1 2.222V3.5h14V2.222zM1 4.5v.5h14v-.5H1zm0 1.5v6h1V6H1zm2 0v6h1V6H3zm2 0v6h1V6H5zm2 0v6h1V6H7zm2 0v6h1V6H9zm2 0v6h1V6h-1zm2 0v6h1V6h-1zM.5 13a.5.5 0 0 0 0 1h15a.5.5 0 0 0 0-1z"/>
                                        </svg>
                                        Bank Details (used on invoice PDFs)
                                    </h2>
                                    {bankOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                </div>
                                {bankOpen && (
                                    <>
                                    <div className="form-grid" style={{ padding: "20px", border: "1px solid #ccc" , borderBottom: "none" }}>
                                        {[
                                            ["beneficiary", "Beneficiary Name"],
                                            ["bankName", "Bank Name"],
                                            ["address", "Bank Address"],
                                            ["branch", "Branch"],
                                            ["accountNumber", "Account No."],
                                            ["swiftCode", "SWIFT Code"],
                                        ].map(([name, label]) => (
                                            <div className="form-field" key={name}>
                                                <label>{label}</label>
                                                <input
                                                    name={name}
                                                    value={form.bankDetails[name] || ""}
                                                    onChange={e => setForm(p => ({ ...p, bankDetails: { ...p.bankDetails, [name]: e.target.value } }))}
                                                    placeholder={label}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="form-grid" style={{ padding: "0 20px 20px 20px", border: "1px solid #ccc", borderTop: "none" }}>
                                        {(form.companyType === "Overseas" ? OVERSEAS_CODE_TYPES : DOMESTIC_CODE_TYPES).map(type => (
                                            <div className="form-field" key={type}>
                                                <label>{type}</label>
                                                <input
                                                    value={codeValue(form.bankDetails.codes, type)}
                                                    onChange={e => setForm(p => ({
                                                        ...p,
                                                        bankDetails: { ...p.bankDetails, codes: withCodeValue(p.bankDetails.codes, type, e.target.value) },
                                                    }))}
                                                    placeholder="Optional"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    </>
                                )}
                            </div>

                            <div style={{ margin: "0 2rem 2rem 2rem" }}>
                                <div className="sub-card-header" onClick={() => setSignatureOpen(o => !o)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <h2 style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <FiEdit size={15} />
                                        Signature and Stamp
                                    </h2>
                                    {signatureOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                </div>
                                {signatureOpen && (
                                    <div className="form-grid" style={{ padding: "20px", border: "1px solid #ccc" , gridTemplateColumns: "repeat(3,1fr)" }}>
                                        <div className="form-field" style={{ justifyContent: "flex-start" }}>
                                            <label>Signer Name</label>
                                            <input
                                                name="signerName"
                                                value={form.signerName}
                                                onChange={e => setForm(p => ({ ...p, signerName: e.target.value }))}
                                                placeholder="e.g. Prashant Suthar"
                                            />
                                        </div>
                                        <div className="form-field">
                                            <label>Company Stamp</label>
                                            <div className="upload-controls">
                                                {stampPreview && <img src={stampPreview} alt="company stamp" style={{ width: 30, height: 30, objectFit: "cover", margin: "0 4px", flexShrink: 0 }} />}
                                                <label htmlFor="comp-stamp" className="custom-file-upload">Choose Stamp</label>
                                                <input type="file" id="comp-stamp" className="file-input" accept="image/*"
                                                    onChange={e => { const f = e.target.files[0]; if (f) { setStampFile(f); setStampPreview(URL.createObjectURL(f)); } }} />
                                                <span className="file-name">{stampFile ? stampFile.name : "No file chosen"}</span>
                                            </div>
                                            <p className="upload-info">Max 1 MB · JPG, PNG, WEBP</p>
                                        </div>
                                        <div className="form-field">
                                            <label>Signer Signature</label>
                                            <div className="upload-controls">
                                                {signaturePreview && <img src={signaturePreview} alt="signer signature" style={{ width: 30, height: 30, objectFit: "cover", margin: "0 4px", flexShrink: 0 }} />}
                                                <label htmlFor="comp-signature" className="custom-file-upload">Choose Signature</label>
                                                <input type="file" id="comp-signature" className="file-input" accept="image/*"
                                                    onChange={e => { const f = e.target.files[0]; if (f) { setSignatureFile(f); setSignaturePreview(URL.createObjectURL(f)); } }} />
                                                <span className="file-name">{signatureFile ? signatureFile.name : "No file chosen"}</span>
                                            </div>
                                            <p className="upload-info">Max 1 MB · JPG, PNG, WEBP</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div style={{ margin: "0 2rem 2rem 2rem" }}>
                                <div className="sub-card-header" onClick={() => setPastRecordsOpen(o => !o)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <h2 style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <TbHistory size={15} />
                                        Past Records Link
                                    </h2>
                                    {pastRecordsOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                </div>
                                {pastRecordsOpen && (
                                    <div style={{ padding: "20px", border: "1px solid #ccc" }}>
                                        <input
                                            type="url"
                                            name="pastRecordsLink"
                                            value={form.pastRecordsLink}
                                            onChange={e => setForm(p => ({ ...p, pastRecordsLink: e.target.value }))}
                                            placeholder="https://sharepoint.com/..."
                                            style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 13, border: "1px solid #d1d5db", outline: "none" }}
                                        />
                                        <p style={{ fontSize: 11, color: "#999", marginTop: 4 }}>URL shown as a book card in the Past Records page for users of this company.</p>
                                    </div>
                                )}
                            </div>

                            {mode !== "add" && (
                                <div style={{ margin: "0 2rem 2rem 2rem" }}>
                                    <div className="sub-card-header" onClick={() => setIncentiveOpen(o => !o)} style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <h2 style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <FiEdit size={15} />
                                            Coordinator Incentive Settings
                                        </h2>
                                        {incentiveOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                    </div>
                                    {incentiveOpen && (
                                        <div style={{ padding: "20px", border: "1px solid #ccc" }}>
                                            {incentive === null && <p style={{ fontSize: 12, color: "#888" }}>Loading&hellip;</p>}
                                            {incentive?.__denied && (
                                                <p style={{ fontSize: 12, color: "#b91c1c" }}>You don't have permission to manage incentive settings for this company.</p>
                                            )}
                                            {incentive && !incentive.__denied && (
                                                <>
                                                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 16 }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={incentive.enabled}
                                                            onChange={e => setIncentive(p => ({ ...p, enabled: e.target.checked }))}
                                                        />
                                                        Enable coordinator incentive for this company
                                                    </label>

                                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, opacity: incentive.enabled ? 1 : 0.5 }}>
                                                        <TierTableEditor
                                                            label="Others (non-India vendor)"
                                                            tiers={incentive.othersTiers}
                                                            onChange={(tiers) => setIncentive(p => ({ ...p, othersTiers: tiers }))}
                                                        />
                                                        <TierTableEditor
                                                            label="India"
                                                            tiers={incentive.indiaTiers}
                                                            onChange={(tiers) => setIncentive(p => ({ ...p, indiaTiers: tiers }))}
                                                        />
                                                    </div>

                                                    <div className="form-grid" style={{ marginTop: 16, opacity: incentive.enabled ? 1 : 0.5 }}>
                                                        <div className="form-field">
                                                            <label>Manmonth rate (&#8377;/win)</label>
                                                            <input type="number" min={0} value={incentive.manmonthRate}
                                                                onChange={e => setIncentive(p => ({ ...p, manmonthRate: Number(e.target.value) }))} />
                                                        </div>
                                                        <div className="form-field">
                                                            <label>Manmonth gate &mdash; Others (%)</label>
                                                            <input type="number" min={0} max={100} value={incentive.manmonthGateOthers}
                                                                onChange={e => setIncentive(p => ({ ...p, manmonthGateOthers: Number(e.target.value) }))} />
                                                        </div>
                                                        <div className="form-field">
                                                            <label>Manmonth gate &mdash; India (%)</label>
                                                            <input type="number" min={0} max={100} value={incentive.manmonthGateIndia}
                                                                onChange={e => setIncentive(p => ({ ...p, manmonthGateIndia: Number(e.target.value) }))} />
                                                        </div>
                                                        <div className="form-field">
                                                            <label>Combined won floor</label>
                                                            <input type="number" min={0} value={incentive.combinedWonFloor}
                                                                onChange={e => setIncentive(p => ({ ...p, combinedWonFloor: Number(e.target.value) }))} />
                                                        </div>
                                                    </div>

                                                    <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #eee", opacity: incentive.enabled ? 1 : 0.5 }}>
                                                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Quarterly incentive (financial-year quarters)</div>
                                                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 10 }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={incentive.quarterlyEnabled !== false}
                                                                onChange={e => setIncentive(p => ({ ...p, quarterlyEnabled: e.target.checked }))}
                                                            />
                                                            Enable quarterly incentive
                                                        </label>
                                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, opacity: incentive.quarterlyEnabled !== false ? 1 : 0.5 }}>
                                                            <div className="form-field">
                                                                <label>Minimum quarter offer cost to qualify (&#8377; lakh)</label>
                                                                <input type="number" min={0} step={0.5}
                                                                    value={(incentive.quarterlyMinRevenueINR ?? 2100000) / LAKH}
                                                                    onChange={e => setIncentive(p => ({ ...p, quarterlyMinRevenueINR: Math.round(Number(e.target.value) * LAKH) }))} />
                                                                <p style={{ fontSize: 11, color: "#888", margin: "6px 0 0" }}>
                                                                    Below this, no quarterly incentive. At or above it, the bands apply stepped across the whole offer cost (offered rate &times; inspection days in the quarter).
                                                                </p>
                                                            </div>
                                                            {incentive.quarterlySlabs?.length > 0 && (
                                                                <QuarterlySlabEditor
                                                                    slabs={incentive.quarterlySlabs}
                                                                    onChange={(slabs) => setIncentive(p => ({ ...p, quarterlySlabs: slabs }))}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="action-btn primary"
                                                        disabled={incentiveSaving}
                                                        onClick={handleSaveIncentive}
                                                        style={{ marginTop: 16 }}
                                                    >
                                                        {incentiveSaving ? "Saving…" : "Save Incentive Settings"}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                    </fieldset>

                    <div className="form-footer" style={{ margin: 0, padding: "12px 16px", background: "#faf9f8", borderTop: "1px solid #e2e2e2" }}>
                        {isView ? (
                            <button type="button" className="save-btn" onClick={() => navigate(`/company-management/${company.id}/edit`)}
                                style={{ background: "#d53e40", color: "#fff", borderRadius: 0, border: "none" }}>
                                <FiEdit size={13} /> Edit Company
                            </button>
                        ) : (
                            <button type="submit" className="save-btn" disabled={saving} style={{ background: saving ? "#ccc" : "#d53e40", color: '#fff', borderRadius: 0, border: 'none' }}>
                                {saving ? "Saving…" : mode === "add" ? "Create Company" : "Update Company"}
                            </button>
                        )}
                        <button type="button" className="cancel-btn"
                            onClick={() => mode === "edit" ? navigate(`/company-management/${company.id}`) : backToList()}>
                            {isView ? "Back" : "Cancel"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CompanyForm;
